"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArchive,
  FaCheck,
  FaChevronRight,
  FaClipboardList,
  FaComments,
  FaEdit,
  FaExternalLinkAlt,
  FaPlus,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import Header from "../../../components/ManagementHeader";
import Footer from "../../../components/Footer";
import { supabase } from "../../../supabaseClient";

const APPLICATION_STATUSES = ["Received", "Under Review", "Accepted", "Denied"];

function formatDate(value, includeTime = false) {
  if (!value) return "Not set";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
  });
}

function normaliseQuestions(value) {
  if (!Array.isArray(value)) return [];

  return value.map((question, index) => {
    if (typeof question === "string") {
      return {
        id: `legacy-${index}`,
        label: question,
        type: "long",
        required: true,
      };
    }

    return {
      id: question?.id || `question-${index}`,
      label: question?.label || question?.question || `Question ${index + 1}`,
      type: question?.type || "long",
      required: question?.required !== false,
      options: Array.isArray(question?.options) ? question.options : [],
    };
  });
}

function StatusBadge({ status }) {
  const key = String(status || "Received").toLowerCase().replace(/\s+/g, "-");
  return <span className={`pguh-recruitment-status pguh-recruitment-status--${key}`}>{status}</span>;
}

export default function ManagementVacancies() {
  const [vacancies, setVacancies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [allApplications, setAllApplications] = useState([]);
  const [activeVacancy, setActiveVacancy] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [chat, setChat] = useState([]);
  const [chatMessage, setChatMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [savingDecision, setSavingDecision] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const chatChannelRef = useRef(null);

  useEffect(() => {
    fetchVacancies();

    return () => cleanupChatSubscription();
  }, []);

  async function fetchVacancies() {
    setLoading(true);
    setError("");

    const [vacancyResult, applicationResult] = await Promise.all([
      supabase.from("dc_vacancies").select("*").order("created_at", { ascending: false }),
      supabase
        .from("dc_applications")
        .select("id, vacancy_id, status, created_at")
        .order("created_at", { ascending: false }),
    ]);

    if (vacancyResult.error) {
      console.error("Unable to load vacancies:", vacancyResult.error);
      setError(vacancyResult.error.message || "Vacancies could not be loaded.");
      setVacancies([]);
    } else {
      setVacancies(vacancyResult.data || []);
    }

    if (applicationResult.error) {
      console.error("Unable to load application totals:", applicationResult.error);
      setAllApplications([]);
    } else {
      setAllApplications(applicationResult.data || []);
    }

    setLoading(false);
  }

  const applicationCounts = useMemo(() => {
    const counts = {};
    allApplications.forEach((application) => {
      if (!counts[application.vacancy_id]) {
        counts[application.vacancy_id] = {
          total: 0,
          received: 0,
          review: 0,
          accepted: 0,
        };
      }

      const bucket = counts[application.vacancy_id];
      bucket.total += 1;
      if (application.status === "Received") bucket.received += 1;
      if (application.status === "Under Review") bucket.review += 1;
      if (application.status === "Accepted") bucket.accepted += 1;
    });
    return counts;
  }, [allApplications]);

  const filteredVacancies = useMemo(() => {
    const query = search.trim().toLowerCase();

    return vacancies.filter((vacancy) => {
      const matchesSearch =
        !query ||
        [vacancy.title, vacancy.department, vacancy.location]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

      const matchesStatus = statusFilter === "All" || vacancy.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [vacancies, search, statusFilter]);

  async function loadApplications(vacancy) {
    setApplicationsLoading(true);
    setError("");
    setActiveVacancy(vacancy);
    setSelectedApplication(null);
    setChat([]);
    cleanupChatSubscription();

    const { data, error: loadError } = await supabase
      .from("dc_applications")
      .select("*")
      .eq("vacancy_id", vacancy.id)
      .order("created_at", { ascending: false });

    if (loadError) {
      console.error("Unable to load applications:", loadError);
      setError(loadError.message || "Applications could not be loaded.");
      setApplications([]);
    } else {
      setApplications(data || []);
    }

    setApplicationsLoading(false);
  }

  async function openApplication(application) {
    setSelectedApplication(application);
    setChat([]);
    cleanupChatSubscription();

    const { data, error: chatError } = await supabase
      .from("dc_application_chat")
      .select("*")
      .eq("application_id", application.id)
      .order("created_at", { ascending: true });

    if (chatError) {
      console.error("Unable to load application chat:", chatError);
    } else {
      setChat(data || []);
    }

    subscribeToChat(application.id);
  }

  function cleanupChatSubscription() {
    if (chatChannelRef.current) {
      supabase.removeChannel(chatChannelRef.current);
      chatChannelRef.current = null;
    }
  }

  function subscribeToChat(applicationId) {
    cleanupChatSubscription();

    const channel = supabase
      .channel(`dc_application_chat_${applicationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "dc_application_chat",
          filter: `application_id=eq.${applicationId}`,
        },
        (payload) => setChat((current) => [...current, payload.new]),
      )
      .subscribe();

    chatChannelRef.current = channel;
  }

  async function sendChatMessage() {
    const message = chatMessage.trim();
    if (!message || !selectedApplication) return;

    const { error: sendError } = await supabase.rpc("ruht_send_application_chat_message", {
      p_application_id: selectedApplication.id,
      p_message: message,
    });

    if (sendError) {
      setError(sendError.message || "The message could not be sent.");
      return;
    }

    setChatMessage("");
  }

  async function setDecision(nextStatus) {
    if (!selectedApplication || savingDecision) return;

    setSavingDecision(true);
    setError("");

    const { data, error: updateError } = await supabase
      .from("dc_applications")
      .update({ status: nextStatus })
      .eq("id", selectedApplication.id)
      .select("*")
      .single();

    if (updateError) {
      setError(updateError.message || "The application status could not be updated.");
    } else {
      setSelectedApplication(data);
      setApplications((current) => current.map((item) => (item.id === data.id ? data : item)));
      setAllApplications((current) =>
        current.map((item) => (item.id === data.id ? { ...item, status: data.status } : item)),
      );
    }

    setSavingDecision(false);
  }

  async function toggleVacancy(vacancy) {
    const isOpen = vacancy.status === "Open" && vacancy.is_active;
    const next = isOpen
      ? { status: "Closed", is_active: false }
      : { status: "Open", is_active: true };

    const { error: updateError } = await supabase
      .from("dc_vacancies")
      .update(next)
      .eq("id", vacancy.id);

    if (updateError) {
      setError(updateError.message || "The vacancy status could not be changed.");
      return;
    }

    fetchVacancies();
  }

  async function deleteVacancy(vacancy) {
    const confirmed = window.confirm(
      `Delete “${vacancy.title}”?\n\nThis removes the vacancy. Existing applications should be retained/exported first if they are required.`,
    );

    if (!confirmed) return;

    const { error: deleteError } = await supabase
      .from("dc_vacancies")
      .delete()
      .eq("id", vacancy.id);

    if (deleteError) {
      setError(deleteError.message || "The vacancy could not be deleted.");
      return;
    }

    if (activeVacancy?.id === vacancy.id) {
      setActiveVacancy(null);
      setApplications([]);
      setSelectedApplication(null);
      cleanupChatSubscription();
    }

    fetchVacancies();
  }

  const customQuestions = normaliseQuestions(activeVacancy?.questions);
  const submittedAnswers = selectedApplication?.submitted_answers || {};

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-1 pguh-recruitment-page">
        <div className="nhsuk-width-container">
          <section className="pguh-recruitment-hero">
            <div>
              <span className="pguh-eyebrow">Management recruitment</span>
              <h1>Vacancies & applications</h1>
              <p>
                Build structured application forms, publish vacancies and review every response in one place.
              </p>
            </div>
            <Link className="nhsuk-button pguh-recruitment-hero__button" to="/intranet/management/vacancies/new">
              <FaPlus aria-hidden="true" />
              Create vacancy
            </Link>
          </section>

          {error && (
            <div className="nhsuk-error-summary pguh-recruitment-error" role="alert">
              <h2 className="nhsuk-error-summary__title">There is a problem</h2>
              <div className="nhsuk-error-summary__body">{error}</div>
            </div>
          )}

          <section className="pguh-recruitment-stats" aria-label="Recruitment summary">
            <div>
              <span>Vacancies</span>
              <strong>{vacancies.length}</strong>
              <small>{vacancies.filter((item) => item.status === "Open" && item.is_active).length} currently open</small>
            </div>
            <div>
              <span>Applications</span>
              <strong>{allApplications.length}</strong>
              <small>{allApplications.filter((item) => item.status === "Received").length} awaiting review</small>
            </div>
            <div>
              <span>Under review</span>
              <strong>{allApplications.filter((item) => item.status === "Under Review").length}</strong>
              <small>Applications being assessed</small>
            </div>
            <div>
              <span>Accepted</span>
              <strong>{allApplications.filter((item) => item.status === "Accepted").length}</strong>
              <small>Successful applications</small>
            </div>
          </section>

          <section className="pguh-recruitment-panel">
            <div className="pguh-recruitment-panel__header">
              <div>
                <span className="pguh-eyebrow">Vacancy register</span>
                <h2>Published and draft vacancies</h2>
              </div>
              <div className="pguh-recruitment-filters">
                <label className="pguh-recruitment-search">
                  <FaSearch aria-hidden="true" />
                  <span className="nhsuk-u-visually-hidden">Search vacancies</span>
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search vacancies"
                  />
                </label>
                <select
                  aria-label="Filter vacancy status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option>All</option>
                  <option>Open</option>
                  <option>Closed</option>
                  <option>Draft</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="pguh-recruitment-loading">Loading recruitment data…</div>
            ) : filteredVacancies.length === 0 ? (
              <div className="pguh-recruitment-empty">
                <FaClipboardList aria-hidden="true" />
                <h3>No vacancies match this view</h3>
                <p>Create a vacancy or change the search/filter above.</p>
              </div>
            ) : (
              <div className="pguh-recruitment-vacancy-list">
                {filteredVacancies.map((vacancy) => {
                  const counts = applicationCounts[vacancy.id] || {
                    total: 0,
                    received: 0,
                    review: 0,
                    accepted: 0,
                  };
                  const questionCount = normaliseQuestions(vacancy.questions).length;
                  const open = vacancy.status === "Open" && vacancy.is_active;

                  return (
                    <article className="pguh-recruitment-vacancy" key={vacancy.id}>
                      <div className="pguh-recruitment-vacancy__main">
                        <div className="pguh-recruitment-vacancy__title-row">
                          <div>
                            <StatusBadge status={open ? "Open" : vacancy.status || "Closed"} />
                            <h3>{vacancy.title}</h3>
                          </div>
                          <span className="pguh-recruitment-count">
                            {counts.total} application{counts.total === 1 ? "" : "s"}
                          </span>
                        </div>

                        <p className="pguh-recruitment-vacancy__description">
                          {vacancy.description || "No vacancy summary has been added yet."}
                        </p>

                        <dl className="pguh-recruitment-meta">
                          <div>
                            <dt>Department</dt>
                            <dd>{vacancy.department || "Not specified"}</dd>
                          </div>
                          <div>
                            <dt>Location</dt>
                            <dd>{vacancy.location || "Not specified"}</dd>
                          </div>
                          <div>
                            <dt>Closes</dt>
                            <dd>{formatDate(vacancy.closing_date)}</dd>
                          </div>
                          <div>
                            <dt>Questions</dt>
                            <dd>4 fixed + {questionCount} custom</dd>
                          </div>
                        </dl>

                        <div className="pguh-recruitment-mini-stats">
                          <span>{counts.received} new</span>
                          <span>{counts.review} reviewing</span>
                          <span>{counts.accepted} accepted</span>
                        </div>
                      </div>

                      <div className="pguh-recruitment-vacancy__actions">
                        <Link
                          className="pguh-recruitment-action pguh-recruitment-action--primary"
                          to={`/intranet/management/vacancies/${vacancy.id}`}
                        >
                          <FaEdit aria-hidden="true" /> Edit form
                        </Link>
                        <Link
                          className="pguh-recruitment-action"
                          to={`/intranet/management/vacancies/${vacancy.id}/applications`}
                        >
                          <FaClipboardList aria-hidden="true" /> View applications
                        </Link>
                        <Link
                          className="pguh-recruitment-action"
                          to={`/vacancies/${vacancy.id}`}
                        >
                          <FaExternalLinkAlt aria-hidden="true" /> Preview
                        </Link>
                        <button className="pguh-recruitment-action" onClick={() => toggleVacancy(vacancy)}>
                          {open ? <FaArchive aria-hidden="true" /> : <FaCheck aria-hidden="true" />}
                          {open ? "Close" : "Reopen"}
                        </button>
                        <button
                          className="pguh-recruitment-action pguh-recruitment-action--danger"
                          onClick={() => deleteVacancy(vacancy)}
                        >
                          <FaTrash aria-hidden="true" /> Delete
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {activeVacancy && (
            <section className="pguh-recruitment-panel pguh-recruitment-applications">
              <div className="pguh-recruitment-panel__header">
                <div>
                  <span className="pguh-eyebrow">Application review</span>
                  <h2>{activeVacancy.title}</h2>
                </div>
                <button
                  className="pguh-recruitment-close"
                  onClick={() => {
                    setActiveVacancy(null);
                    setApplications([]);
                    setSelectedApplication(null);
                    cleanupChatSubscription();
                  }}
                >
                  <FaTimes aria-hidden="true" /> Close review
                </button>
              </div>

              <div className="pguh-recruitment-review-layout">
                <aside className="pguh-recruitment-app-list">
                  <div className="pguh-recruitment-app-list__heading">
                    <strong>Applications</strong>
                    <span>{applications.length}</span>
                  </div>

                  {applicationsLoading ? (
                    <p>Loading applications…</p>
                  ) : applications.length === 0 ? (
                    <div className="pguh-recruitment-empty pguh-recruitment-empty--compact">
                      <FaUser aria-hidden="true" />
                      <p>No applications have been submitted.</p>
                    </div>
                  ) : (
                    applications.map((application) => (
                      <button
                        key={application.id}
                        className={`pguh-recruitment-app-item ${
                          selectedApplication?.id === application.id ? "is-selected" : ""
                        }`}
                        onClick={() => openApplication(application)}
                      >
                        <span>
                          <strong>{application.roleplay_name || application.discord_username || "Applicant"}</strong>
                          <small>{application.discord_username || "No Discord username"}</small>
                        </span>
                        <span>
                          <StatusBadge status={application.status || "Received"} />
                          <FaChevronRight aria-hidden="true" />
                        </span>
                      </button>
                    ))
                  )}
                </aside>

                <div className="pguh-recruitment-review">
                  {!selectedApplication ? (
                    <div className="pguh-recruitment-empty">
                      <FaClipboardList aria-hidden="true" />
                      <h3>Select an application</h3>
                      <p>Choose an applicant from the list to review their answers and decision status.</p>
                    </div>
                  ) : (
                    <>
                      <div className="pguh-recruitment-review__top">
                        <div>
                          <span className="pguh-eyebrow">Applicant</span>
                          <h3>{selectedApplication.roleplay_name || selectedApplication.discord_username}</h3>
                          <p>Submitted {formatDate(selectedApplication.created_at, true)}</p>
                        </div>
                        <label>
                          <span>Application status</span>
                          <select
                            value={selectedApplication.status || "Received"}
                            disabled={savingDecision}
                            onChange={(event) => setDecision(event.target.value)}
                          >
                            {APPLICATION_STATUSES.map((status) => (
                              <option key={status}>{status}</option>
                            ))}
                          </select>
                        </label>
                      </div>

                      <section className="pguh-recruitment-answer-section">
                        <h4>Applicant details</h4>
                        <dl className="pguh-recruitment-answer-grid">
                          <div>
                            <dt>Roblox username</dt>
                            <dd>{selectedApplication.roblox_username || "Not provided"}</dd>
                          </div>
                          <div>
                            <dt>Discord username</dt>
                            <dd>{selectedApplication.discord_username || "Not provided"}</dd>
                          </div>
                          <div>
                            <dt>Email</dt>
                            <dd>{selectedApplication.email || "Not provided"}</dd>
                          </div>
                          <div>
                            <dt>Roleplay Name</dt>
                            <dd>{selectedApplication.roleplay_name || "Not provided"}</dd>
                          </div>
                        </dl>
                      </section>

                      <section className="pguh-recruitment-answer-section">
                        <h4>Application answers</h4>
                        {customQuestions.length === 0 ? (
                          <p className="pguh-recruitment-muted">This vacancy has no custom questions.</p>
                        ) : (
                          <div className="pguh-recruitment-custom-answers">
                            {customQuestions.map((question, index) => {
                              const answer = submittedAnswers[question.id];
                              const displayAnswer = Array.isArray(answer)
                                ? answer.join(", ")
                                : answer || "No answer provided";

                              return (
                                <article key={question.id}>
                                  <span>Question {index + 1}</span>
                                  <h5>{question.label}</h5>
                                  <p>{displayAnswer}</p>
                                </article>
                              );
                            })}
                          </div>
                        )}
                      </section>

                      <section className="pguh-recruitment-chat-section">
                        <div className="pguh-recruitment-chat-heading">
                          <div>
                            <FaComments aria-hidden="true" />
                            <h4>Application chat</h4>
                          </div>
                          <span>{chat.length} messages</span>
                        </div>

                        <div className="pguh-recruitment-chat-log" aria-live="polite">
                          {chat.length === 0 ? (
                            <p>No messages yet.</p>
                          ) : (
                            chat.map((message) => (
                              <div
                                key={message.id}
                                className={`pguh-recruitment-chat-message pguh-recruitment-chat-message--${message.sender}`}
                              >
                                <strong>{message.sender === "staff" ? "Management" : "Applicant"}</strong>
                                <p>{message.message}</p>
                                <time>{formatDate(message.created_at, true)}</time>
                              </div>
                            ))
                          )}
                        </div>

                        <div className="pguh-recruitment-chat-compose">
                          <label>
                            <span className="nhsuk-u-visually-hidden">Message applicant</span>
                            <textarea
                              rows="2"
                              value={chatMessage}
                              onChange={(event) => setChatMessage(event.target.value)}
                              placeholder="Write a message to the applicant…"
                              maxLength={1500}
                            />
                          </label>
                          <button onClick={sendChatMessage} disabled={!chatMessage.trim()}>
                            Send message
                          </button>
                        </div>
                      </section>
                    </>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />

      <style>{`
        .pguh-recruitment-page {
          padding: 36px 0 64px;
          background: var(--nhs-light-grey);
        }
        .pguh-recruitment-hero {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 32px;
          margin-bottom: 24px;
          padding: 30px 34px;
          border-left: 8px solid var(--nhs-light-blue);
          color: #fff;
          background: linear-gradient(115deg, var(--nhs-dark-blue), var(--nhs-blue));
          box-shadow: var(--pguh-shadow);
        }
        .pguh-recruitment-hero h1,
        .pguh-recruitment-hero p,
        .pguh-recruitment-hero .pguh-eyebrow { color: #fff; }
        .pguh-recruitment-hero h1 { margin: 0 0 8px; }
        .pguh-recruitment-hero p { max-width: 760px; margin: 0; }
        .pguh-recruitment-hero__button {
          display: inline-flex;
          flex: 0 0 auto;
          align-items: center;
          gap: 9px;
          margin: 0;
          color: #fff !important;
        }
        .pguh-recruitment-error { margin-bottom: 24px; }
        .pguh-recruitment-stats {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
          margin-bottom: 24px;
        }
        .pguh-recruitment-stats > div {
          display: flex;
          min-height: 112px;
          justify-content: center;
          flex-direction: column;
          padding: 18px 20px;
          border: 1px solid var(--pguh-border);
          border-left: 5px solid var(--nhs-light-blue);
          background: #fff;
          box-shadow: var(--pguh-shadow);
        }
        .pguh-recruitment-stats span { color: var(--nhs-dark-grey); font-weight: 700; }
        .pguh-recruitment-stats strong { color: var(--nhs-dark-blue); font-size: 1.8rem; line-height: 1.05; }
        .pguh-recruitment-stats small { margin-top: 5px; color: var(--nhs-mid-grey); }
        .pguh-recruitment-panel {
          margin-bottom: 24px;
          border: 1px solid var(--pguh-border);
          background: #fff;
          box-shadow: var(--pguh-shadow);
        }
        .pguh-recruitment-panel__header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 24px;
          padding: 24px 26px 20px;
          border-bottom: 1px solid var(--pguh-border);
        }
        .pguh-recruitment-panel__header h2 { margin: 0; }
        .pguh-recruitment-filters { display: flex; align-items: center; gap: 10px; }
        .pguh-recruitment-search { position: relative; display: flex; align-items: center; }
        .pguh-recruitment-search svg { position: absolute; left: 12px; color: var(--nhs-dark-grey); }
        .pguh-recruitment-search input,
        .pguh-recruitment-filters select {
          min-height: 44px;
          border: 2px solid var(--nhs-dark-grey);
          background: #fff;
        }
        .pguh-recruitment-search input { width: 250px; padding: 8px 10px 8px 38px; }
        .pguh-recruitment-filters select { min-width: 130px; padding: 7px 34px 7px 10px; }
        .pguh-recruitment-loading { padding: 40px 26px; color: var(--nhs-dark-grey); font-weight: 700; }
        .pguh-recruitment-empty {
          display: grid;
          min-height: 220px;
          place-items: center;
          align-content: center;
          padding: 30px;
          color: var(--nhs-dark-grey);
          text-align: center;
        }
        .pguh-recruitment-empty > svg { margin-bottom: 12px; color: var(--nhs-mid-grey); font-size: 2rem; }
        .pguh-recruitment-empty h3 { margin: 0 0 6px; }
        .pguh-recruitment-empty p { margin: 0; }
        .pguh-recruitment-empty--compact { min-height: 150px; }
        .pguh-recruitment-vacancy-list { display: grid; }
        .pguh-recruitment-vacancy {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 190px;
          gap: 24px;
          padding: 24px 26px;
          border-bottom: 1px solid var(--pguh-border);
        }
        .pguh-recruitment-vacancy:last-child { border-bottom: 0; }
        .pguh-recruitment-vacancy__title-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; }
        .pguh-recruitment-vacancy__title-row h3 { margin: 6px 0 0; font-size: 1.25rem; }
        .pguh-recruitment-count { flex: 0 0 auto; color: var(--nhs-dark-blue); font-weight: 700; }
        .pguh-recruitment-vacancy__description { max-width: 800px; margin: 12px 0 18px; color: var(--nhs-dark-grey); }
        .pguh-recruitment-meta {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
          margin: 0;
        }
        .pguh-recruitment-meta > div { padding: 10px 12px; background: var(--nhs-light-grey); }
        .pguh-recruitment-meta dt { color: var(--nhs-dark-grey); font-size: .76rem; font-weight: 700; text-transform: uppercase; }
        .pguh-recruitment-meta dd { margin: 2px 0 0; font-weight: 700; }
        .pguh-recruitment-mini-stats { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
        .pguh-recruitment-mini-stats span { padding: 5px 8px; background: #e8f1f8; color: var(--nhs-dark-blue); font-size: .8rem; font-weight: 700; }
        .pguh-recruitment-vacancy__actions { display: grid; align-content: start; gap: 8px; }
        .pguh-recruitment-action {
          display: inline-flex;
          min-height: 40px;
          align-items: center;
          justify-content: flex-start;
          gap: 8px;
          padding: 8px 10px;
          border: 2px solid var(--nhs-blue);
          color: var(--nhs-blue) !important;
          background: #fff;
          font-weight: 700;
          text-decoration: none;
          cursor: pointer;
        }
        .pguh-recruitment-action:hover { color: #fff !important; background: var(--nhs-blue); text-decoration: none; }
        .pguh-recruitment-action--primary { color: #fff !important; background: var(--nhs-blue); }
        .pguh-recruitment-action--danger { border-color: var(--nhs-red); color: var(--nhs-red) !important; }
        .pguh-recruitment-action--danger:hover { background: var(--nhs-red); }
        .pguh-recruitment-status {
          display: inline-flex;
          min-height: 24px;
          align-items: center;
          padding: 3px 8px;
          color: #163b21;
          background: #cce5d6;
          font-size: .72rem;
          font-weight: 700;
          text-transform: uppercase;
        }
        .pguh-recruitment-status--closed,
        .pguh-recruitment-status--denied { color: #6f120b; background: #f7d4d1; }
        .pguh-recruitment-status--draft { color: #425563; background: #e8edee; }
        .pguh-recruitment-status--received { color: #003087; background: #d8e7f3; }
        .pguh-recruitment-status--under-review { color: #594300; background: #fff1b8; }
        .pguh-recruitment-status--accepted { color: #00401e; background: #cce5d6; }
        .pguh-recruitment-close {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          border: 0;
          color: var(--nhs-blue);
          background: transparent;
          font-weight: 700;
          text-decoration: underline;
          cursor: pointer;
        }
        .pguh-recruitment-review-layout {
          display: grid;
          grid-template-columns: 320px minmax(0, 1fr);
          min-height: 560px;
        }
        .pguh-recruitment-app-list { border-right: 1px solid var(--pguh-border); background: #f7fafc; }
        .pguh-recruitment-app-list__heading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 18px;
          border-bottom: 1px solid var(--pguh-border);
        }
        .pguh-recruitment-app-list__heading span { display: grid; min-width: 28px; height: 28px; place-items: center; background: var(--nhs-blue); color: #fff; font-weight: 700; }
        .pguh-recruitment-app-item {
          display: flex;
          width: 100%;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 15px 18px;
          border: 0;
          border-bottom: 1px solid var(--pguh-border);
          border-left: 5px solid transparent;
          background: #fff;
          text-align: left;
          cursor: pointer;
        }
        .pguh-recruitment-app-item:hover,
        .pguh-recruitment-app-item.is-selected { border-left-color: var(--nhs-blue); background: #e8f1f8; }
        .pguh-recruitment-app-item > span:first-child { display: flex; min-width: 0; flex-direction: column; }
        .pguh-recruitment-app-item small { overflow: hidden; color: var(--nhs-dark-grey); text-overflow: ellipsis; white-space: nowrap; }
        .pguh-recruitment-app-item > span:last-child { display: flex; align-items: center; gap: 8px; }
        .pguh-recruitment-review { min-width: 0; padding: 26px; }
        .pguh-recruitment-review__top { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; padding-bottom: 20px; border-bottom: 1px solid var(--pguh-border); }
        .pguh-recruitment-review__top h3 { margin: 0; font-size: 1.55rem; }
        .pguh-recruitment-review__top p { margin: 4px 0 0; color: var(--nhs-dark-grey); }
        .pguh-recruitment-review__top label { display: flex; min-width: 190px; flex-direction: column; gap: 5px; font-weight: 700; }
        .pguh-recruitment-review__top select { min-height: 44px; border: 2px solid var(--nhs-dark-grey); padding: 7px; background: #fff; }
        .pguh-recruitment-answer-section { padding: 22px 0; border-bottom: 1px solid var(--pguh-border); }
        .pguh-recruitment-answer-section h4 { margin: 0 0 14px; }
        .pguh-recruitment-answer-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin: 0; }
        .pguh-recruitment-answer-grid > div { padding: 13px 14px; border-left: 4px solid var(--nhs-light-blue); background: var(--nhs-light-grey); }
        .pguh-recruitment-answer-grid dt { color: var(--nhs-dark-grey); font-size: .78rem; font-weight: 700; }
        .pguh-recruitment-answer-grid dd { margin: 3px 0 0; overflow-wrap: anywhere; font-weight: 700; }
        .pguh-recruitment-custom-answers { display: grid; gap: 12px; }
        .pguh-recruitment-custom-answers article { padding: 15px 16px; border: 1px solid var(--pguh-border); border-left: 5px solid var(--nhs-blue); }
        .pguh-recruitment-custom-answers span { color: var(--nhs-dark-grey); font-size: .75rem; font-weight: 700; text-transform: uppercase; }
        .pguh-recruitment-custom-answers h5 { margin: 3px 0 8px; font-size: 1rem; }
        .pguh-recruitment-custom-answers p { margin: 0; white-space: pre-wrap; }
        .pguh-recruitment-muted { color: var(--nhs-dark-grey); }
        .pguh-recruitment-chat-section { padding-top: 22px; }
        .pguh-recruitment-chat-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 12px; }
        .pguh-recruitment-chat-heading > div { display: flex; align-items: center; gap: 9px; }
        .pguh-recruitment-chat-heading h4 { margin: 0; }
        .pguh-recruitment-chat-heading span { color: var(--nhs-dark-grey); font-size: .8rem; }
        .pguh-recruitment-chat-log { max-height: 300px; padding: 14px; overflow-y: auto; border: 1px solid var(--pguh-border); background: var(--nhs-light-grey); }
        .pguh-recruitment-chat-log > p { margin: 0; color: var(--nhs-dark-grey); text-align: center; }
        .pguh-recruitment-chat-message { width: fit-content; max-width: 80%; margin-bottom: 10px; padding: 9px 11px; border-left: 4px solid var(--nhs-mid-grey); background: #fff; }
        .pguh-recruitment-chat-message--staff { margin-left: auto; border-left-color: var(--nhs-blue); background: #e8f1f8; }
        .pguh-recruitment-chat-message p { margin: 2px 0; white-space: pre-wrap; }
        .pguh-recruitment-chat-message time { color: var(--nhs-mid-grey); font-size: .7rem; }
        .pguh-recruitment-chat-compose { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 10px; margin-top: 10px; }
        .pguh-recruitment-chat-compose textarea { width: 100%; min-height: 62px; resize: vertical; border: 2px solid var(--nhs-dark-grey); padding: 8px 10px; }
        .pguh-recruitment-chat-compose button { min-width: 130px; border: 0; border-bottom: 4px solid #00401e; color: #fff; background: var(--nhs-green); font-weight: 700; cursor: pointer; }
        .pguh-recruitment-chat-compose button:disabled { opacity: .55; }
        @media (max-width: 1050px) {
          .pguh-recruitment-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .pguh-recruitment-vacancy { grid-template-columns: 1fr; }
          .pguh-recruitment-vacancy__actions { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .pguh-recruitment-meta { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .pguh-recruitment-review-layout { grid-template-columns: 280px minmax(0, 1fr); }
        }
        @media (max-width: 780px) {
          .pguh-recruitment-page { padding-top: 20px; }
          .pguh-recruitment-hero,
          .pguh-recruitment-panel__header,
          .pguh-recruitment-review__top { align-items: flex-start; flex-direction: column; }
          .pguh-recruitment-hero { padding: 24px 20px; }
          .pguh-recruitment-hero__button { width: 100%; justify-content: center; }
          .pguh-recruitment-filters { width: 100%; align-items: stretch; flex-direction: column; }
          .pguh-recruitment-search input,
          .pguh-recruitment-filters select { width: 100%; }
          .pguh-recruitment-stats { grid-template-columns: 1fr 1fr; }
          .pguh-recruitment-review-layout { grid-template-columns: 1fr; }
          .pguh-recruitment-app-list { border-right: 0; border-bottom: 1px solid var(--pguh-border); }
          .pguh-recruitment-answer-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 520px) {
          .pguh-recruitment-stats { grid-template-columns: 1fr; }
          .pguh-recruitment-vacancy__actions,
          .pguh-recruitment-meta { grid-template-columns: 1fr; }
          .pguh-recruitment-vacancy__title-row { flex-direction: column; }
          .pguh-recruitment-review { padding: 18px; }
          .pguh-recruitment-chat-compose { grid-template-columns: 1fr; }
          .pguh-recruitment-chat-compose button { min-height: 44px; }
        }
      `}</style>
    </div>
  );
}
